// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Content/ContentTravelForm.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ProgressSpinner } from 'primereact/progressspinner';
import { OrderList } from 'primereact/orderlist';
import { Editor } from 'primereact/editor';
import { API_URLS, API_ENDPOINTS } from '../../api/api';
import '../../css/content-travel-form.scss';

const ContentTravelForm = ({ travelData, mode, onCancel, onSave }) => {
  const [formData, setFormData] = useState({
    Job_ID: travelData?.Job_ID || '',
    Job_Name: travelData?.Job_Name || '',
    Job_Date: travelData?.Job_Date || '',
    Job_Details: travelData?.Job_Details || '',
    Job_Detail2: travelData?.Job_Detail2 || '',
    Job_Detail3: travelData?.Job_Detail3 || '',
    Job_Maps: travelData?.Job_Maps || '',
    JobImg_img: travelData?.images?.[0]?.JobImg_img || '',
    JobImg_img_original: travelData?.images?.[0]?.JobImg_img_original || '',
    JobImg_file: null,
  });
  const [images, setImages] = useState(travelData?.images || []);
  const [isUploading, setIsUploading] = useState(false);

  const commonHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };

  // Custom Toolbar for Editor
  const customToolbar = [
    [{ 'header': [1, 2, 3, false] }], // Headers
    ['bold', 'italic', 'underline'],  // Basic formatting
    [{ 'list': 'ordered' }, { 'list': 'bullet' }], // Lists
    ['link'], // Links only
    ['clean'] // Remove formatting
  ];

  // ฟังก์ชันช่วยอัปเดตค่าจาก Editor
  const handleEditorChange = (fieldName, htmlValue) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: htmlValue || ''
    }));
  };

  // Fetch specific Job_ID data in edit mode
  const fetchSingleTravelData = async (jobId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TRAVEL_VIEW}&Job_ID=${jobId}`, {
        method: 'GET',
        headers: commonHeaders,
      });
      if (!response.ok) throw new Error('Failed to fetch travel data for Job_ID');
      const data = await response.json();
      const jobData = Array.isArray(data.jobs) && data.jobs.length > 0 ? data.jobs[0] : {};
      setFormData((prev) => ({
        ...prev,
        Job_Name: jobData.Job_Name || prev.Job_Name,
        Job_Date: jobData.Job_Date || prev.Job_Date,
        Job_Details: jobData.Job_Details || prev.Job_Details,
        Job_Detail2: jobData.Job_Detail2 || prev.Job_Detail2,
        Job_Detail3: jobData.Job_Detail3 || prev.Job_Detail3,
        Job_Maps: jobData.Job_Maps || prev.Job_Maps,
      }));
      setImages(jobData.images || []);
    } catch (error) {
      console.error('Fetch single travel error:', error);
    }
  };

  // Update image sort order via API
  const updateImageSortOrder = async (newImages) => {
    if (mode !== 'edit' || !formData.Job_ID) return;

    try {
      const updatePromises = newImages.map((image, index) => {
        const payload = {
          JobImg_ID: image.JobImg_ID,
          Job_ID: formData.Job_ID,
          JobImg_sort: index,
        };
        return fetch(API_ENDPOINTS.TRAVEL_UPDATE_IMAGE, {
          method: 'PUT',
          headers: commonHeaders,
          body: JSON.stringify(payload),
        }).then(response => {
          if (!response.ok) throw new Error(`Failed to update sort order for JobImg_ID: ${image.JobImg_ID}`);
          return response.json();
        });
      });
      await Promise.all(updatePromises);
      console.log('Image sort order updated successfully');
      fetchSingleTravelData(formData.Job_ID);
    } catch (error) {
      console.error('Error updating image sort order:', error);
      alert('Failed to update image sort order');
      fetchSingleTravelData(formData.Job_ID);
    }
  };

  useEffect(() => {
    if (mode === 'edit' && formData.Job_ID) {
      fetchSingleTravelData(formData.Job_ID);
    }
  }, [mode, formData.Job_ID]);

  const handleDeleteImage = async (JobImg_ID) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      try {
        const deleteUrl = API_ENDPOINTS.TRAVEL_DELETE_IMAGE.replace('{JobImg_ID}', JobImg_ID);
        const response = await fetch(deleteUrl, {
          method: 'GET',
          headers: commonHeaders,
        });
        if (!response.ok) {
          alert("Failed to delete image");
          return;
        }
        setImages(images.filter(image => image.JobImg_ID !== JobImg_ID));
        console.log("Image deleted successfully");
        if (mode === 'edit' && formData.Job_ID) {
          fetchSingleTravelData(formData.Job_ID);
        }
      } catch (error) {
        console.error("Error deleting image:", error);
        alert("Error deleting image");
      }
    }
  };

  const resizedImageTemplate = (item) => (
    <div className="p-2 flex align-items-center gap-3">
      <img
        src={item.JobImg_img}
        alt="Resized Preview"
        className="img-thumbnail"
        style={{ maxWidth: '200px' }}
      />
      <button
        type="button"
        className="btn btn-danger btn-sm"
        onClick={() => handleDeleteImage(item.JobImg_ID)}
      >
        Delete
      </button>
    </div>
  );

  const originalImageTemplate = (item) => (
    <div className="p-2 flex align-items-center gap-3">
      <img
        src={item.JobImg_img_original}
        alt="Original Preview"
        className="img-thumbnail"
        style={{ maxWidth: '200px' }}
      />
      <button
        type="button"
        className="btn btn-danger btn-sm"
        onClick={() => handleDeleteImage(item.JobImg_ID)}
      >
        Delete
      </button>
    </div>
  );

  const handleChange = async (e) => {
    // กรณีเป็น input หรือ file upload
    if (e.target) {
      const { name, value, files } = e.target;
      if (name === 'JobImg_file' && files[0]) {
        setIsUploading(true);
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', files[0]);
          const response = await fetch(API_URLS.IMAGE_UPLOAD, {
            method: 'POST',
            body: formDataUpload,
          });
          if (!response.ok) throw new Error(`Failed to upload image: ${response.statusText}`);
          const result = await response.json();
          if (!result.resizedFileURLs || !result.originalFileURLs) throw new Error('Invalid response format');

          const updatedFormData = {
            ...formData,
            JobImg_file: files[0],
            JobImg_img: result.resizedFileURLs[0],
            JobImg_img_original: result.originalFileURLs[0],
          };
          setFormData(updatedFormData);

          if (updatedFormData.Job_ID && updatedFormData.JobImg_img && updatedFormData.JobImg_img_original) {
            const imagePayload = {
              JobImg_img: updatedFormData.JobImg_img,
              JobImg_img_original: updatedFormData.JobImg_img_original,
              Job_ID: updatedFormData.Job_ID,
              StatusJobImg_ID: 1,
              JobImg_sort: images.length,
            };
            const imageResponse = await fetch(API_ENDPOINTS.TRAVEL_INSERT_IMAGE, {
              method: 'POST',
              headers: commonHeaders,
              body: JSON.stringify(imagePayload),
            });

            if (!imageResponse.ok) {
              console.warn('Failed to insert travel image');
              setImages((prev) => [...prev, { ...imagePayload, JobImg_ID: `temp-${Date.now()}` }]);
            } else {
              const newImageResponse = await imageResponse.json();
              setImages((prev) => [
                ...prev,
                {
                  JobImg_ID: newImageResponse.JobImg_ID || `temp-${Date.now()}`,
                  JobImg_img: updatedFormData.JobImg_img,
                  JobImg_img_original: updatedFormData.JobImg_img_original,
                  JobImg_sort: images.length,
                  ...newImageResponse,
                },
              ]);
            }
            if (mode === 'edit' && formData.Job_ID) fetchSingleTravelData(formData.Job_ID);
          }
        } catch (error) {
          console.error('Image upload error:', error);
          alert(`Failed to upload image: ${error.message}`);
          setFormData({
            ...formData,
            JobImg_file: null,
            JobImg_img: '',
            JobImg_img_original: '',
          });
        } finally {
          setIsUploading(false);
        }
      } else {
        // text, date, textarea
        setFormData({ ...formData, [name]: value });
      }
    }
  };

  const handleImageOrderChange = (e) => {
    const newImages = e.value;
    setImages(newImages);
    updateImageSortOrder(newImages);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, images });
  };

  return (
    <div className="content-travel-form container">
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="Job_Name" className="form-label">Travel Name:</label>
          <input
            type="text"
            id="Job_Name"
            name="Job_Name"
            className="form-control"
            value={formData.Job_Name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="Job_Date" className="form-label">Date:</label>
          <input
            type="date"
            id="Job_Date"
            name="Job_Date"
            className="form-control"
            value={formData.Job_Date}
            onChange={handleChange}
            required
          />
        </div>

        {/* Editor สำหรับ Job_Details */}
        <div className="mb-3">
          <label htmlFor="Job_Details" className="form-label">Details:</label>
          <Editor
            id="Job_Details"
            value={formData.Job_Details}
            // เปลี่ยนมาใช้ handleEditorChange
            onTextChange={(e) => handleEditorChange('Job_Details', e.htmlValue)}
            headerTemplate={<div className="custom-editor-toolbar" />}
            style={{ height: 'auto', minHeight: '150px' }}
            modules={{ toolbar: customToolbar }}
          />
        </div>

        {/* Editor สำหรับ Job_Detail2 */}
        <div className="mb-3">
          <label htmlFor="Job_Detail2" className="form-label">Detail 2:</label>
          <Editor
            id="Job_Detail2"
            value={formData.Job_Detail2}
            onTextChange={(e) => handleEditorChange('Job_Detail2', e.htmlValue)}
            headerTemplate={<div className="custom-editor-toolbar" />}
            style={{ height: 'auto', minHeight: '150px' }}
            modules={{ toolbar: customToolbar }}
          />
        </div>

        {/* Editor สำหรับ Job_Detail3 */}
        <div className="mb-3">
          <label htmlFor="Job_Detail3" className="form-label">Detail 3:</label>
          <Editor
            id="Job_Detail3"
            value={formData.Job_Detail3}
            onTextChange={(e) => handleEditorChange('Job_Detail3', e.htmlValue)}
            headerTemplate={<div className="custom-editor-toolbar" />}
            style={{ height: 'auto', minHeight: '150px' }}
            modules={{ toolbar: customToolbar }}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="Job_Maps" className="form-label">Maps (HTML):</label>
          <textarea
            id="Job_Maps"
            name="Job_Maps"
            className="form-control"
            value={formData.Job_Maps}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="JobImg_img" className="form-label">Image URL or Upload:</label>
          <input
            type="text"
            id="JobImg_img"
            name="JobImg_img"
            className="form-control"
            value={formData.JobImg_img}
            onChange={handleChange}
            placeholder="Paste image URL or upload below"
          />
          <input
            type="file"
            id="JobImg_file"
            name="JobImg_file"
            className="form-control mt-2"
            onChange={handleChange}
            accept="image/*"
            disabled={isUploading}
          />
          {isUploading && (
            <div className="mt-2">
              <ProgressSpinner
                style={{ width: '30px', height: '30px' }}
                strokeWidth="8"
                fill="transparent"
                animationDuration=".5s"
              />
              <span className="ml-2">Uploading...</span>
            </div>
          )}
          {images && images.length > 0 ? (
            <>
              <label className="form-label mt-2">Resized Images Preview:</label>
              <OrderList
                dataKey="JobImg_ID"
                value={images}
                onChange={handleImageOrderChange}
                itemTemplate={resizedImageTemplate}
                header="Resized Images"
                dragdrop
              />
            </>
          ) : (
            formData.JobImg_img && (
              <img
                src={formData.JobImg_img}
                alt="Travel Preview"
                className="img-thumbnail mt-2"
                style={{ maxWidth: '200px' }}
              />
            )
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="JobImg_img_original" className="form-label">Original Image URL:</label>
          <input
            type="text"
            id="JobImg_img_original"
            name="JobImg_img_original"
            className="form-control"
            value={formData.JobImg_img_original}
            onChange={handleChange}
            placeholder="Original image URL will appear here"
            readOnly
          />
          {images && images.length > 0 ? (
            <>
              <label className="form-label mt-2">Original Images Preview:</label>
              <OrderList
                dataKey="JobImg_ID"
                value={images}
                onChange={handleImageOrderChange}
                itemTemplate={originalImageTemplate}
                header="Original Images"
                dragdrop
              />
            </>
          ) : (
            formData.JobImg_img_original && (
              <img
                src={formData.JobImg_img_original}
                alt="Original Preview"
                className="img-thumbnail mt-2"
                style={{ maxWidth: '200px' }}
              />
            )
          )}
        </div>

        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-light"
            disabled={isUploading}
          >
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
          <button type="submit" className="btn btn-dark" disabled={isUploading}>
            <FontAwesomeIcon icon={faFloppyDisk} />{' '}
            {mode === 'add' ? 'Add' : 'Update'} Travel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentTravelForm;
